// src/components/Income/WizardAddIncome.js
import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { AVAILABLE_YEARS } from '../../utils/incomeConstants';
import { calculateTaxableIncome, getAbatementDescription } from '../../utils/incomeCalculations';
import './WizardAddIncome.css';

// Types de revenus simplifiés avec leurs champs spécifiques
const INCOME_TYPE_CONFIG = {
  SALARIE: {
    label: 'Salarié',
    icon: '💼',
    category: 'Activité',
    description: 'CDI, CDD, intérim...',
    color: '#3498DB',
    specificFields: [],
    abatementType: 'SALAIRE'
  },
  CHOMAGE: {
    label: 'Chômage',
    icon: '📋',
    category: 'Remplacement',
    description: 'ARE, ASS...',
    color: '#9B59B6',
    specificFields: [
      { key: 'dailyAllowance', label: 'Allocation journalière (€)', type: 'number' },
      { key: 'daysPerMonth', label: 'Jours indemnisés/mois', type: 'number', default: 30 }
    ],
    abatementType: 'CHOMAGE'
  },
  LMNP: {
    label: 'LMNP',
    icon: '🏠',
    category: 'Immobilier',
    description: 'Location meublée non professionnelle',
    color: '#E67E22',
    specificFields: [
      {
        key: 'regime',
        label: 'Régime fiscal',
        type: 'select',
        options: [
          { value: 'MICRO', label: 'Micro-BIC (50% abattement)' },
          { value: 'REEL', label: 'Réel simplifié' }
        ],
        default: 'MICRO'
      }
    ],
    abatementType: (fields) => fields.regime === 'REEL' ? 'LMNP_REEL' : 'LMNP_MICRO'
  },
  EI: {
    label: 'Entreprise individuelle',
    icon: '🏪',
    category: 'Indépendant',
    description: 'Auto-entrepreneur, profession libérale...',
    color: '#27AE60',
    specificFields: [
      {
        key: 'activityType',
        label: 'Type d\'activité',
        type: 'select',
        options: [
          { value: 'BNC', label: 'BNC - Profession libérale' },
          { value: 'BIC_SERVICE', label: 'BIC - Prestations de services' },
          { value: 'BIC_VENTE', label: 'BIC - Vente de marchandises' }
        ],
        default: 'BNC'
      },
      {
        key: 'regime',
        label: 'Régime fiscal',
        type: 'select',
        options: [
          { value: 'MICRO', label: 'Micro-entreprise' },
          { value: 'REEL', label: 'Réel' }
        ],
        default: 'MICRO'
      },
      { key: 'acre', label: 'ACRE (1ère année)', type: 'checkbox', default: false }
    ],
    abatementType: (fields) => {
      if (fields.regime === 'REEL') {
        return fields.activityType === 'BNC' ? 'REEL_BNC' : 'REEL_BIC';
      }
      if (fields.activityType === 'BNC') return 'MICRO_BNC';
      if (fields.activityType === 'BIC_VENTE') return 'MICRO_BIC_VENTE';
      return 'MICRO_BIC_SERVICE';
    }
  },
  SOCIETE_IR: {
    label: 'Quote-part société IR',
    icon: '🏢',
    category: 'Société',
    description: 'SCI, SARL de famille à l\'IR...',
    color: '#34495E',
    specificFields: [],
    requiresEntity: true,
    abatementType: 'SOCIETE_IR'
  }
};

const WizardAddIncome = ({ income, entities = [], onSave, onClose }) => {
  // État du wizard
  const [selectedType, setSelectedType] = useState(income?.income_type || income?.incomeType || null);

  // État du formulaire
  const [formData, setFormData] = useState({
    startDate: income?.start_date ? new Date(income.start_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    endDate: income?.end_date ? new Date(income.end_date).toISOString().split('T')[0] : '',
    linkedEntityId: income?.linked_entity_id || income?.linkedEntityId || null,
    specificFields: income?.specific_fields || income?.specificFields || {}
  });

  // État des montants par année
  const [varyByYear, setVaryByYear] = useState(
    income?.yearly_amounts?.length > 1 || income?.yearlyAmounts?.length > 1
  );
  const [selectedYearTab, setSelectedYearTab] = useState(AVAILABLE_YEARS[0]);
  const [yearlyAmounts, setYearlyAmounts] = useState(() => {
    const amounts = {};
    const existingAmounts = income?.yearly_amounts || income?.yearlyAmounts || [];

    if (existingAmounts.length > 0) {
      existingAmounts.forEach(ya => {
        amounts[ya.year] = {
          amount: ya.amount,
          manualCharges: ya.manual_charges || ya.manualCharges || null
        };
      });
    } else {
      // Par défaut, initialiser l'année courante
      amounts[AVAILABLE_YEARS[0]] = { amount: 0, manualCharges: null };
    }
    return amounts;
  });

  // Montant unique (si varyByYear est false)
  const [singleAmount, setSingleAmount] = useState(() => {
    const existingAmounts = income?.yearly_amounts || income?.yearlyAmounts || [];
    return existingAmounts.length > 0 ? existingAmounts[0].amount : 0;
  });

  // Initialiser les champs spécifiques avec les valeurs par défaut
  useEffect(() => {
    if (selectedType && INCOME_TYPE_CONFIG[selectedType]) {
      const config = INCOME_TYPE_CONFIG[selectedType];
      const defaults = {};
      config.specificFields.forEach(field => {
        if (field.default !== undefined && formData.specificFields[field.key] === undefined) {
          defaults[field.key] = field.default;
        }
      });
      if (Object.keys(defaults).length > 0) {
        setFormData(prev => ({
          ...prev,
          specificFields: { ...prev.specificFields, ...defaults }
        }));
      }
    }
  }, [selectedType]);

  // Calcul du type d'abattement pour les previews
  const getAbatementType = useMemo(() => {
    if (!selectedType) return null;
    const config = INCOME_TYPE_CONFIG[selectedType];
    if (typeof config.abatementType === 'function') {
      return config.abatementType(formData.specificFields);
    }
    return config.abatementType;
  }, [selectedType, formData.specificFields]);

  // Calcul des previews par année
  const calculatePreview = (amount, year) => {
    if (!getAbatementType || amount === 0) {
      return { taxable: 0, abatement: '-' };
    }

    const mockIncome = {
      incomeType: getAbatementType,
      grossAmount: amount,
      expenses: formData.specificFields.charges || 0
    };

    const taxable = calculateTaxableIncome(mockIncome, year);
    const abatement = getAbatementDescription(getAbatementType, year);

    return { taxable, abatement };
  };

  const handleSpecificFieldChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      specificFields: {
        ...prev.specificFields,
        [key]: value
      }
    }));
  };

  const handleYearAmountChange = (year, value) => {
    setYearlyAmounts(prev => ({
      ...prev,
      [year]: {
        ...prev[year],
        amount: parseFloat(value) || 0
      }
    }));
  };

  const handleSubmit = () => {
    if (!selectedType) return;

    // Préparer les montants par année
    let amounts = [];
    if (varyByYear) {
      amounts = Object.entries(yearlyAmounts)
        .filter(([_, data]) => data.amount > 0)
        .map(([year, data]) => ({
          year: parseInt(year),
          amount: data.amount,
          manualCharges: data.manualCharges
        }));
    } else {
      // Montant unique appliqué à toutes les années de la période
      const startYear = new Date(formData.startDate).getFullYear();
      const endYear = formData.endDate
        ? new Date(formData.endDate).getFullYear()
        : AVAILABLE_YEARS[0];

      for (let year = Math.min(startYear, endYear); year <= Math.max(startYear, endYear); year++) {
        if (AVAILABLE_YEARS.includes(year)) {
          amounts.push({
            year,
            amount: singleAmount,
            manualCharges: null
          });
        }
      }
    }

    const incomeData = {
      income_type: selectedType,
      start_date: formData.startDate,
      end_date: formData.endDate || null,
      linked_entity_id: selectedType === 'SOCIETE_IR' ? formData.linkedEntityId : null,
      specific_fields: formData.specificFields,
      yearly_amounts: amounts
    };

    onSave(incomeData);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Grouper les types par catégorie
  const typesByCategory = useMemo(() => {
    const grouped = {};
    Object.entries(INCOME_TYPE_CONFIG).forEach(([key, config]) => {
      if (!grouped[config.category]) {
        grouped[config.category] = [];
      }
      grouped[config.category].push({ key, ...config });
    });
    return grouped;
  }, []);

  const currentConfig = selectedType ? INCOME_TYPE_CONFIG[selectedType] : null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content wizard-income" onClick={e => e.stopPropagation()}>
        <div className="wizard-header">
          <h3>{income ? 'Modifier le revenu' : 'Ajouter un revenu'}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="wizard-body">
          {/* Étape 1: Sélection du type */}
          <div className="wizard-section">
            <h4>Type de revenu</h4>
            <div className="type-cards-container">
              {Object.entries(typesByCategory).map(([category, types]) => (
                <div key={category} className="type-category">
                  <span className="category-label">{category}</span>
                  <div className="type-cards">
                    {types.map(type => (
                      <div
                        key={type.key}
                        className={`type-card ${selectedType === type.key ? 'selected' : ''}`}
                        style={{ '--card-color': type.color }}
                        onClick={() => setSelectedType(type.key)}
                      >
                        <span className="type-icon">{type.icon}</span>
                        <span className="type-label">{type.label}</span>
                        <span className="type-description">{type.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Champs conditionnels après sélection du type */}
          {selectedType && (
            <>
              {/* Période */}
              <div className="wizard-section">
                <h4>Période</h4>
                <div className="period-fields">
                  <div className="form-group">
                    <label>Date de début</label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={e => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Date de fin (optionnel)</label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={e => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                    <span className="help-text">Laisser vide si en cours</span>
                  </div>
                </div>
              </div>

              {/* Champs spécifiques au type */}
              {currentConfig.specificFields.length > 0 && (
                <div className="wizard-section">
                  <h4>Détails {currentConfig.label}</h4>
                  <div className="specific-fields">
                    {currentConfig.specificFields.map(field => (
                      <div key={field.key} className="form-group">
                        {field.type === 'select' ? (
                          <>
                            <label>{field.label}</label>
                            <select
                              value={formData.specificFields[field.key] || field.default || ''}
                              onChange={e => handleSpecificFieldChange(field.key, e.target.value)}
                            >
                              {field.options.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </>
                        ) : field.type === 'checkbox' ? (
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.specificFields[field.key] || false}
                              onChange={e => handleSpecificFieldChange(field.key, e.target.checked)}
                            />
                            {field.label}
                          </label>
                        ) : (
                          <>
                            <label>{field.label}</label>
                            <input
                              type={field.type}
                              value={formData.specificFields[field.key] || field.default || ''}
                              onChange={e => handleSpecificFieldChange(
                                field.key,
                                field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value
                              )}
                            />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lien entité pour SOCIETE_IR */}
              {currentConfig.requiresEntity && (
                <div className="wizard-section">
                  <h4>Société liée</h4>
                  <div className="form-group">
                    <label>Sélectionner la société</label>
                    <select
                      value={formData.linkedEntityId || ''}
                      onChange={e => setFormData(prev => ({ ...prev, linkedEntityId: parseInt(e.target.value) || null }))}
                    >
                      <option value="">-- Sélectionner --</option>
                      {entities
                        .filter(e => e.taxRegime === 'IR' || e.tax_regime === 'IR')
                        .map(entity => (
                          <option key={entity.id || entity.entity_id} value={entity.id || entity.entity_id}>
                            {entity.entityName || entity.entity_name}
                          </option>
                        ))
                      }
                    </select>
                    {entities.filter(e => e.taxRegime === 'IR' || e.tax_regime === 'IR').length === 0 && (
                      <span className="help-text warning">Aucune société à l'IR dans ce scénario</span>
                    )}
                  </div>
                </div>
              )}

              {/* Montants */}
              <div className="wizard-section">
                <h4>Montants</h4>

                <label className="checkbox-label vary-checkbox">
                  <input
                    type="checkbox"
                    checked={varyByYear}
                    onChange={e => setVaryByYear(e.target.checked)}
                  />
                  Montants différents selon les années
                </label>

                {!varyByYear ? (
                  /* Montant unique */
                  <div className="single-amount-section">
                    <div className="form-group">
                      <label>Montant annuel brut</label>
                      <input
                        type="number"
                        value={singleAmount}
                        onChange={e => setSingleAmount(parseFloat(e.target.value) || 0)}
                        min="0"
                        step="100"
                      />
                    </div>
                    {singleAmount > 0 && (
                      <div className="calculation-preview">
                        <div className="preview-row">
                          <span>Abattement:</span>
                          <span>{calculatePreview(singleAmount, AVAILABLE_YEARS[0]).abatement}</span>
                        </div>
                        <div className="preview-row result">
                          <span>Imposable:</span>
                          <span className="taxable">{formatCurrency(calculatePreview(singleAmount, AVAILABLE_YEARS[0]).taxable)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Montants par année */
                  <div className="yearly-amounts-section">
                    <div className="year-tabs">
                      {AVAILABLE_YEARS.map(year => (
                        <button
                          key={year}
                          className={`year-tab ${selectedYearTab === year ? 'active' : ''}`}
                          onClick={() => setSelectedYearTab(year)}
                        >
                          {year}
                          {yearlyAmounts[year]?.amount > 0 && (
                            <span className="year-indicator">●</span>
                          )}
                        </button>
                      ))}
                    </div>

                    <div className="year-content">
                      <div className="form-group">
                        <label>Montant brut {selectedYearTab}</label>
                        <input
                          type="number"
                          value={yearlyAmounts[selectedYearTab]?.amount || 0}
                          onChange={e => handleYearAmountChange(selectedYearTab, e.target.value)}
                          min="0"
                          step="100"
                        />
                      </div>

                      {(yearlyAmounts[selectedYearTab]?.amount || 0) > 0 && (
                        <div className="calculation-preview">
                          <div className="preview-row">
                            <span>Abattement:</span>
                            <span>{calculatePreview(yearlyAmounts[selectedYearTab]?.amount, selectedYearTab).abatement}</span>
                          </div>
                          <div className="preview-row result">
                            <span>Imposable {selectedYearTab}:</span>
                            <span className="taxable">
                              {formatCurrency(calculatePreview(yearlyAmounts[selectedYearTab]?.amount, selectedYearTab).taxable)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Résumé des années renseignées */}
                    <div className="years-summary">
                      <h5>Récapitulatif</h5>
                      <div className="summary-grid">
                        {AVAILABLE_YEARS.filter(y => yearlyAmounts[y]?.amount > 0).map(year => (
                          <div key={year} className="summary-row">
                            <span className="year">{year}</span>
                            <span className="amount">{formatCurrency(yearlyAmounts[year]?.amount)}</span>
                            <span className="taxable">→ {formatCurrency(calculatePreview(yearlyAmounts[year]?.amount, year).taxable)}</span>
                          </div>
                        ))}
                        {Object.values(yearlyAmounts).every(y => !y?.amount || y.amount === 0) && (
                          <span className="no-data">Aucune année renseignée</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="wizard-footer">
          <button type="button" className="cancel-btn" onClick={onClose}>
            Annuler
          </button>
          <button
            type="button"
            className="save-btn"
            onClick={handleSubmit}
            disabled={!selectedType || (!varyByYear && singleAmount === 0) || (varyByYear && Object.values(yearlyAmounts).every(y => !y?.amount))}
          >
            {income ? 'Enregistrer' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default WizardAddIncome;
